import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
    Plus,
    Trash2,
    Edit,
    Loader2,
    AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function ExpenseCategoryManagementPage() {
    const { t } = useLanguage();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: '#3B82F6'
    });
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const PRESET_COLORS = [
        '#EF4444', // red-500
        '#F97316', // orange-500
        '#EAB308', // yellow-500
        '#22C55E', // green-500
        '#06B6D4', // cyan-500
        '#3B82F6', // blue-500
        '#8B5CF6', // violet-500
        '#EC4899', // pink-500
        '#6B7280', // gray-500
    ];

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/expense-categories`, { headers });
            setCategories(response.data.data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
            toast.error(t('failedFetchCategories') || 'Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setFormData({
                name: category.name,
                description: category.description || '',
                color: category.color || '#3B82F6'
            });
        } else {
            setEditingCategory(null);
            setFormData({
                name: '',
                description: '',
                color: '#3B82F6'
            });
        }
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setEditingCategory(null);
        setFormData({
            name: '',
            description: '',
            color: '#3B82F6'
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error(t('categoryNameRequired') || 'Category name is required');
            return;
        }

        try {
            if (editingCategory) {
                await axios.put(
                    `${API_URL}/expense-categories/${editingCategory.id}`,
                    formData,
                    { headers }
                );
                toast.success(t('categoryUpdated') || 'Category updated successfully');
            } else {
                await axios.post(
                    `${API_URL}/expense-categories`,
                    formData,
                    { headers }
                );
                toast.success(t('categoryCreated') || 'Category created successfully');
            }
            handleCloseDialog();
            fetchCategories();
        } catch (error) {
            console.error('Failed to save category:', error);
            if (error.response?.data?.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error(t('failedSaveCategory') || 'Failed to save category');
            }
        }
    };

    const handleDeleteClick = (category) => {
        setCategoryToDelete(category);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!categoryToDelete) return;

        try {
            await axios.delete(
                `${API_URL}/expense-categories/${categoryToDelete.id}`,
                { headers }
            );
            toast.success(t('categoryDeleted') || 'Category deleted successfully');
            setDeleteConfirmOpen(false);
            setCategoryToDelete(null);
            fetchCategories();
        } catch (error) {
            console.error('Failed to delete category:', error);
            if (error.response?.data?.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error(t('failedDeleteCategory') || 'Failed to delete category');
            }
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">
                            {t('expenseCategoryManagement') || 'Expense Categories'}
                        </h1>
                        <p className="text-muted-foreground">
                            {t('manageCategoriesSubtitle') || 'Organize and manage your expense categories'}
                        </p>
                    </div>
                    <Button onClick={() => handleOpenDialog()}>
                        <Plus className="h-4 w-4 mr-2" />
                        {t('addCategory') || 'Add Category'}
                    </Button>
                </div>

                {/* Categories Table */}
                <Card>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-8"></TableHead>
                                    <TableHead>{t('name')}</TableHead>
                                    <TableHead>{t('description')}</TableHead>
                                    <TableHead className="w-20">{t('color')}</TableHead>
                                    <TableHead className="w-20">{t('actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="h-24 text-center">
                                            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                                        </TableCell>
                                    </TableRow>
                                ) : categories.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                                            {t('noCategories') || 'No categories found'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    categories.map((category) => (
                                        <TableRow key={category.id}>
                                            <TableCell>
                                                <div
                                                    className="w-6 h-6 rounded-md border"
                                                    style={{ backgroundColor: category.color }}
                                                    title={category.color}
                                                />
                                            </TableCell>
                                            <TableCell className="font-medium">{category.name}</TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {category.description || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    style={{
                                                        backgroundColor: category.color + '20',
                                                        borderColor: category.color,
                                                        color: category.color
                                                    }}
                                                >
                                                    {category.color}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleOpenDialog(category)}
                                                        title={t('edit') || 'Edit'}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="text-destructive hover:text-destructive hover:bg-red-50 dark:hover:bg-red-950/30"
                                                        onClick={() => handleDeleteClick(category)}
                                                        title={t('delete') || 'Delete'}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>

            {/* Add/Edit Category Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>
                            {editingCategory
                                ? (t('editCategory') || 'Edit Category')
                                : (t('addNewCategory') || 'Add New Category')}
                        </DialogTitle>
                        <DialogDescription>
                            {editingCategory
                                ? (t('updateCategoryDetails') || 'Update category information')
                                : (t('createNewCategory') || 'Create a new expense category')}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="name">{t('name')} *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('placeholderCategoryName') || 'e.g. Office Supplies'}
                                required
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="description">{t('description')}</Label>
                            <Input
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder={t('placeholderCategoryDesc') || 'e.g. Office supplies and equipment'}
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="color">{t('color')} *</Label>
                            <div className="space-y-3">
                                <div className="flex gap-2 flex-wrap">
                                    {PRESET_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            className={`w-10 h-10 rounded-md border-2 transition-all ${
                                                formData.color === color
                                                    ? 'border-foreground'
                                                    : 'border-transparent'
                                            }`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => setFormData({ ...formData, color })}
                                            title={color}
                                        />
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        id="customColor"
                                        value={formData.color}
                                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                        className="h-10 w-20 rounded border cursor-pointer"
                                    />
                                    <Input
                                        type="text"
                                        value={formData.color}
                                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                        placeholder="#000000"
                                        className="flex-1"
                                    />
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={handleCloseDialog}>
                                {t('cancel') || 'Cancel'}
                            </Button>
                            <Button type="submit">
                                {editingCategory
                                    ? (t('updateCategory') || 'Update')
                                    : (t('createCategory') || 'Create')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-destructive" />
                            {t('confirmDelete') || 'Confirm Delete'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            {t('deleteConfirmMessage') || 'Are you sure you want to delete this category?'}
                        </p>
                        {categoryToDelete && (
                            <div className="p-3 bg-muted rounded-lg">
                                <p className="font-medium">{categoryToDelete.name}</p>
                                {categoryToDelete.description && (
                                    <p className="text-sm text-muted-foreground">{categoryToDelete.description}</p>
                                )}
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                            {t('deleteWarning') || 'This action cannot be undone.'}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setDeleteConfirmOpen(false)}
                        >
                            {t('cancel') || 'Cancel'}
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={handleConfirmDelete}
                        >
                            {t('delete') || 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
